import axios from "axios";
import { has } from "lodash";
import { env } from "../config";
import { beginForegroundRequest } from "./foregroundRequests";

function getCookie(name) {
  return document.cookie
    .split('; ')
    .find(row => row.startsWith(name + '='))
    ?.split('=')[1] || null;
}
if (!env.REACT_APP_API_BASE_URL) {
  throw new Error("REACT_APP_API_BASE_URL is a required environment variable");
}

/**
 * Provide a ready-to-use instance of the XHR api
 * Tokens are now handled via HTTP-only secure cookies
 */
const xhrService = axios.create({
  baseURL: env.REACT_APP_API_BASE_URL,
  withCredentials: true, // Enable sending cookies with requests
});

/**
 * SW2-2458: count foreground requests, so background photo uploads (flagged `backgroundUpload`)
 * can wait for them (see foregroundRequests).
 *
 * Registered before the auth middleware on purpose: axios runs request interceptors
 * last-registered first, so this runs last, just before the request is sent, and a throw in
 * another interceptor cannot leave a request counted that never went out.
 */
xhrService.interceptors.request.use(function countForegroundRequest(config) {
  if (!config.backgroundUpload) {
    const release = beginForegroundRequest();

    config.releaseForeground = release;
    // axios 0.18 rejects a cancelled request with a Cancel that has no config, so the response
    // interceptor below cannot release it; the token's own promise can.
    if (config.cancelToken) config.cancelToken.promise.then(release);
  }

  return config;
});

xhrService.interceptors.response.use(
  (response) => {
    if (response.config && response.config.releaseForeground) response.config.releaseForeground();

    return response;
  },
  (error) => {
    if (error && error.config && error.config.releaseForeground) error.config.releaseForeground();

    return Promise.reject(error);
  },
);

/** Auth middleware */
xhrService.interceptors.request.use(function setBearerToken(config) {
  // Tokens are now stored in HTTP-only secure cookies
  // No need to manually set Authorization header
  // The browser will automatically send the cookie with each request
  
  const divisionId = JSON.parse(localStorage.getItem("divisionId"));

  const csrfToken = getCookie('csrf_token');

  if (csrfToken) {
    config.headers['X-CSRF-TOKEN'] = csrfToken;
  }
  /** @deprecated */
  if (has(config, "withoutDivision")) {
    console.warn(
      "xhrService configuration option `withoutDivision` is deprecated and has no effect.",
    );
  }

  config.headers.divisionId = divisionId ? `${divisionId}` : "";

  if (config.withoutDivision) {
    delete config.withoutDivision;
  }

  return config;
});

export { xhrService };
/**
 * Cancellation, exposed here so callers do not import axios themselves.
 *
 * axios is pinned at 0.18.1, which predates `AbortController` support (`signal`
 * arrived in 0.22), so `CancelToken` is the mechanism. Keeping both halves behind this
 * module means a version bump changes one file rather than every caller: swap these
 * two for an `AbortController` and the call sites keep working.
 */
export function createCancelSource() {
  return axios.CancelToken.source();
}

/** True when a rejection is this client abandoning its own request. */
export function isRequestCancelled(error) {
  return axios.isCancel(error);
}

export default xhrService;
